import { useScrollAnimation } from "../../../hooks/useScrollAnimation";
import { Link } from "react-router-dom";

interface ServiceCardProps {
  id: string;
  icon: string;
  eyebrow: string;
  title: string;
  desc: string;
  benefits: string[];
  included: string[];
  img: string;
  reversed?: boolean;
}

export default function ServiceCard({
  id,
  icon,
  eyebrow,
  title,
  desc,
  benefits,
  included,
  img,
  reversed = false,
}: ServiceCardProps) {
  const { ref, visible } = useScrollAnimation();

  return (
    <section id={id} className="section-pad border-b border-zinc-100 last:border-0" ref={ref}>
      <div className="container-md">
        <div
          className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
            reversed ? "lg:grid-flow-col-dense" : ""
          }`}
        >
          {/* Text */}
          <div
            className={`transition-all duration-700 ${
              visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
            } ${reversed ? "lg:col-start-2" : ""}`}
          >
            <span className="text-accent text-xs font-semibold uppercase tracking-[0.2em] mb-3 block">
              {eyebrow}
            </span>
            <div className="w-12 h-12 flex items-center justify-center bg-accent/10 rounded-xl mb-4">
              <i className={`${icon} text-2xl text-accent`} />
            </div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-zinc-900 leading-tight mb-4">
              {title}
            </h2>
            <p className="text-zinc-600 text-base leading-relaxed mb-8">{desc}</p>

            {/* Benefits */}
            <div className="space-y-3 mb-8">
              {benefits.map((b) => (
                <div key={b} className="flex items-start gap-3">
                  <span className="w-5 h-5 flex items-center justify-center bg-accent/10 rounded-full shrink-0 mt-0.5">
                    <i className="ri-check-line text-accent text-xs" />
                  </span>
                  <span className="text-zinc-700 text-sm">{b}</span>
                </div>
              ))}
            </div>

            {/* What's included */}
            <div className="bg-stone-50 rounded-xl p-5 mb-8">
              <p className="font-semibold text-zinc-800 text-sm mb-3">What&apos;s included:</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {included.map((item) => (
                  <div key={item} className="flex items-center gap-2 text-zinc-600 text-sm">
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-checkbox-circle-line text-accent text-sm" />
                    </span>
                    {item}
                  </div>
                ))}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <Link
                to="/contact"
                className="whitespace-nowrap inline-flex items-center justify-center gap-2 bg-accent hover:bg-accent-dark text-white font-semibold px-6 py-3 rounded-lg text-sm transition-all cursor-pointer"
              >
                Get a Free Quote
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line" />
                </span>
              </Link>
              <Link
                to="/portfolio"
                className="whitespace-nowrap inline-flex items-center justify-center gap-2 border border-zinc-200 hover:border-accent text-zinc-700 hover:text-accent font-semibold px-6 py-3 rounded-lg text-sm transition-all cursor-pointer"
              >
                See our work
              </Link>
            </div>
          </div>

          {/* Image */}
          <div
            className={`transition-all duration-700 delay-200 ${
              visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
            } ${reversed ? "lg:col-start-1 lg:row-start-1" : ""}`}
          >
            <div className="rounded-2xl overflow-hidden w-full h-80 md:h-[420px]">
              <img
                src={img}
                alt={title}
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
